/**
 * KioskConsentStep — the E-Sign consent ceremony on the signer kiosk (Phase A,
 * Bill 2026-07-07). Rendered by SignerKiosk when the FSR pushes a step with
 * stepKind === 'consent'.
 *
 * Mirrors the FSR-side EsignConsentGate reading ceremony (scroll-tracked text,
 * I-agree enabled at >= 80%) but token-authorized against the kiosk endpoints:
 *   GET  /v3/signing/kiosk/consent/text?token=  → { success, text, sha256 }
 *   POST /v3/signing/kiosk/consent              → { success, auditRowId?, ... }
 *
 * No local fallback consent text: the server records consent against its
 * canonical text (sha256-tied audit), so showing different local copy would
 * break the audit tie — on load failure we offer a plain retry instead.
 * No "I prefer paper" button either: the signer can't cancel — the FSR drives
 * the flow, same as the capture steps.
 */

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import type {
  KioskConsentRequest,
  KioskConsentResponse,
} from '@retail-ops/shared-types';
import { ENV } from '@/constants/env';
import { kioskFetch } from './kioskSession';

/** Wire shape of GET /v3/signing/kiosk/consent/text (kiosk-local, not in shared-types). */
interface KioskConsentTextResponse {
  success: boolean;
  text?: string;
  sha256?: string;
  error?: string;
}

export interface KioskConsentStepProps {
  token: string;
  signerName?: string | null;
  /** Pauses the parent's state polling while the consent POST is in flight. */
  onSubmittingChange: (submitting: boolean) => void;
  /** Consent accepted server-side — parent shows the saved card + refetches. */
  onSaved: () => void;
}

const SCROLL_THRESHOLD_PCT = 80;

// ── styles (kiosk conventions — see SignerKiosk.tsx) ─────────────────────
const cardS: CSSProperties = {
  background: '#fff', borderRadius: 12, maxHeight: '82vh', width: '100%',
  boxShadow: '0 10px 36px rgba(0,0,0,0.12)', display: 'flex', flexDirection: 'column', overflow: 'hidden',
};
const headerS: CSSProperties = { padding: '20px 28px 0', flex: '0 0 auto' };
// "Consenting as <Name>" — the first thing on the screen, sized to be read at arm's length
// across a desk on a kiosk tablet, not squinted at.
const signerBadgeS: CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 1,
  padding: '8px 14px', marginBottom: 12,
  background: '#eef4fb', border: '1px solid #cfe0f2',
  borderLeft: '4px solid #2968ad', borderRadius: 8,
};
const signerBadgeLabelS: CSSProperties = {
  fontSize: 11, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: '#5b7ba6',
};
const signerBadgeNameS: CSSProperties = {
  fontSize: 22, fontWeight: 700, color: '#1a406a', lineHeight: 1.2,
};
const titleS: CSSProperties = { margin: 0, fontSize: 20, fontWeight: 700, color: '#1a406a' };
const introS: CSSProperties = { margin: '6px 0 0', fontSize: 14, color: '#475569', lineHeight: 1.5 };
const progressWrapS: CSSProperties = { padding: '12px 28px 0', flex: '0 0 auto' };
const progressTrackS: CSSProperties = { height: 6, background: '#e5e7eb', borderRadius: 999, overflow: 'hidden' };
const progressFillS = (pct: number, ready: boolean): CSSProperties => ({
  width: `${Math.min(100, pct)}%`, height: '100%',
  background: ready ? '#386732' : '#2968ad',
  transition: 'width 120ms ease-out, background 240ms ease-out',
});
const progressLabelS: CSSProperties = { marginTop: 4, fontSize: 12, color: '#475569' };
const scrollerS: CSSProperties = {
  flex: '1 1 auto', overflowY: 'auto', margin: '12px 28px 0', padding: '14px 16px',
  fontSize: 15, lineHeight: 1.6, color: '#1f2937', background: '#f8fafc',
  border: '1px solid #e5e7eb', borderRadius: 8, whiteSpace: 'pre-wrap', minHeight: 160,
};
const loadingS: CSSProperties = { ...scrollerS, background: '#fff', color: '#475569', fontStyle: 'italic' };
const errorS: CSSProperties = {
  background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca',
  borderRadius: 8, padding: '10px 14px', fontSize: 14, margin: '12px 28px 0',
};
const footerS: CSSProperties = { padding: '16px 28px 20px', flex: '0 0 auto', display: 'flex', justifyContent: 'flex-end', gap: 10 };
const agreeBtnS = (enabled: boolean): CSSProperties => ({
  padding: '14px 22px', borderRadius: 8, fontSize: 16, fontWeight: 600, color: '#fff',
  background: enabled ? '#386732' : '#9ca3af',
  border: `1px solid ${enabled ? '#386732' : '#9ca3af'}`,
  cursor: enabled ? 'pointer' : 'not-allowed',
});
const retryBtnS: CSSProperties = {
  padding: '10px 18px', borderRadius: 8, fontSize: 14, fontWeight: 600,
  background: '#2968ad', color: '#fff', border: '1px solid #2968ad', cursor: 'pointer',
};

type StepState = 'loading' | 'load-error' | 'reading' | 'submitting';

export function KioskConsentStep(props: KioskConsentStepProps): JSX.Element {
  const { token, signerName, onSubmittingChange, onSaved } = props;

  const [state, setState] = useState<StepState>('loading');
  const [text, setText] = useState<string | null>(null);
  const [scrollPct, setScrollPct] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Intent = the moment the consent text first rendered on this screen.
  const intentTimestampRef = useRef<string | null>(null);
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const mountedRef = useRef(true);
  // Phones (Bill's 2026-10-13 smoke test): the 82vh card cap squeezed the reading box to two
  // lines under the header, badge and intro. On narrow screens the card grows with the page
  // and the reading box gets a fixed, comfortable height; the page scrolls to "I agree".
  const [narrow, setNarrow] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 640px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 640px)');
    const on = () => setNarrow(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  const card = narrow ? { ...cardS, maxHeight: 'none', overflow: 'visible' } : cardS;
  const scroller = narrow ? { ...scrollerS, flex: '0 0 auto', height: '60vh', margin: '12px 16px 0' } : scrollerS;

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const fetchText = useCallback(async () => {
    setState('loading');
    setError(null);
    try {
      const r = await kioskFetch(
        `${ENV.apiBaseUrl}/v3/signing/kiosk/consent/text?token=${encodeURIComponent(token)}`
      );
      const j = (await r.json().catch(() => null)) as KioskConsentTextResponse | null;
      if (!mountedRef.current) return;
      if (!r.ok || !j?.success || !j.text) {
        setError(
          r.status === 401
            ? 'This signing session is no longer active. Please ask your banker to reconnect this screen.'
            : 'We could not load the consent text. Please try again.'
        );
        setState('load-error');
        return;
      }
      setText(j.text);
      intentTimestampRef.current = new Date().toISOString();
      setState('reading');
    } catch {
      if (!mountedRef.current) return;
      setError('Connection problem — we could not load the consent text. Please try again.');
      setState('load-error');
    }
  }, [token]);

  useEffect(() => { void fetchText(); }, [fetchText]);

  // ── scroll tracking (monotonic max, content-fits → 100) ────────────────
  const recalcScroll = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const max = el.scrollHeight - el.clientHeight;
    if (max <= 0) { setScrollPct(100); return; }
    const pct = Math.min(100, Math.round((el.scrollTop / max) * 100));
    setScrollPct((prev) => Math.max(prev, pct));
  }, []);

  // Once the text mounts, measure once so the no-scroll-needed branch fires.
  useEffect(() => {
    if (state === 'reading') requestAnimationFrame(() => recalcScroll());
  }, [state, recalcScroll]);

  const canAgree = scrollPct >= SCROLL_THRESHOLD_PCT;

  const handleAgree = useCallback(async () => {
    if (!canAgree || state === 'submitting') return;
    setState('submitting');
    setError(null);
    onSubmittingChange(true);
    try {
      const body: KioskConsentRequest = {
        token,
        scrollProgressPct: Math.min(100, Math.max(0, Math.round(scrollPct))),
        intentTimestamp: intentTimestampRef.current ?? new Date().toISOString(),
        signerViewportSize: `${window.innerWidth}x${window.innerHeight}`,
        signerUserAgent: navigator.userAgent,
      };
      const r = await kioskFetch(`${ENV.apiBaseUrl}/v3/signing/kiosk/consent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const j = (await r.json()) as KioskConsentResponse;
      if (!mountedRef.current) return;
      if (j.success) {
        onSaved();
        return;
      }
      if (j.errorCode === 'INSUFFICIENT_SCROLL') {
        setError('Please scroll through the full consent text before agreeing.');
      } else if (j.errorCode === 'PRESENTATION_INVALID') {
        setError('This signing session is no longer active. Please ask your banker to reconnect this screen.');
      } else {
        setError(j.error ?? 'We could not save your consent. Please try again.');
      }
      setState('reading');
    } catch {
      if (!mountedRef.current) return;
      setError('Connection problem — please try again in a moment.');
      setState('reading');
    } finally {
      onSubmittingChange(false);
    }
  }, [canAgree, state, token, scrollPct, onSubmittingChange, onSaved]);

  // ── render ───────────────────────────────────────────────────────────
  return (
    <div style={card}>
      <div style={headerS}>
        {/*
          WHO is consenting, stated first and unmissably (Bill 2026-07-16: "econsent should
          prominently display in header the name of the signer/consenter").

          The name used to be buried mid-sentence — "Hi Sample B Test — please read…". At a
          branch kiosk two or three people consent in turn on the SAME screen, so the person
          picking up the tablet has to be able to tell at a glance that this consent is
          THEIRS. Buried in body copy, it is exactly how a joint owner taps "I agree" on the
          primary's consent without either of them noticing — and an e-consent attributed to
          the wrong person is not a UX slip, it is a bad record.
        */}
        {signerName && (
          <div style={signerBadgeS}>
            <span style={signerBadgeLabelS}>Consenting as</span>
            <span style={signerBadgeNameS}>{signerName}</span>
          </div>
        )}
        <h1 style={titleS}>Before you sign — electronic records and signatures</h1>
        <p style={introS}>
          Please read the consent below, then tap <em>I agree</em> to receive your records and
          disclosures electronically.
        </p>
      </div>

      {state !== 'load-error' && (
        <div style={progressWrapS}>
          <div style={progressTrackS}>
            <div style={progressFillS(scrollPct, canAgree)} />
          </div>
          <div style={progressLabelS}>
            {canAgree
              ? 'Thanks — you have read enough to continue.'
              : `${scrollPct}% read — scroll to the bottom to enable I agree`}
          </div>
        </div>
      )}

      {state === 'loading' ? (
        <div style={loadingS}>Loading the consent text…</div>
      ) : state === 'load-error' ? (
        <div style={{ ...loadingS, fontStyle: 'normal' }}>&nbsp;</div>
      ) : (
        <div
          ref={scrollerRef}
          style={scroller}
          onScroll={recalcScroll}
          tabIndex={0}
          aria-label="Consent text"
        >
          {text}
        </div>
      )}

      {error && <div role="alert" style={errorS}>{error}</div>}

      <div style={footerS}>
        {state === 'load-error' ? (
          <button type="button" style={retryBtnS} onClick={() => void fetchText()}>
            Try again
          </button>
        ) : (
          <button
            type="button"
            style={agreeBtnS(canAgree && state !== 'submitting')}
            disabled={!canAgree || state !== 'reading'}
            onClick={() => void handleAgree()}
          >
            {state === 'submitting'
              ? 'Saving…'
              : 'I agree to receive electronic records and signatures'}
          </button>
        )}
      </div>
    </div>
  );
}

export default KioskConsentStep;
